import { VideoBasicBoldIcon } from '@depop/web-ui-kit/Icons/VideoBasicBoldIcon';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import {
  ProductCard,
  type Props as ProductCardProps,
} from '@/components/ProductCard';
import { ProductCardLike } from '@/components/ProductCard/ProductCardLike';
import { ProductCardAttributes } from '@/components/ProductCardAttributes';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { buildProductImageBasicProps } from '@/modules/pictures/helpers';
import { type RecentlyViewedProduct } from '@/modules/recentlyViewed/types';

type Props = {
  id: RecentlyViewedProduct['id'];
  slug: RecentlyViewedProduct['slug'];
  pricing: RecentlyViewedProduct['pricing'] & { currency: CurrencyCode };
  sizes: RecentlyViewedProduct['sizes'];
  pictures?: RecentlyViewedProduct['pictures'];
  preview: RecentlyViewedProduct['preview'];
  brandName?: RecentlyViewedProduct['brand_name'];
  hasVideo?: RecentlyViewedProduct['has_video'];
  isLiked?: RecentlyViewedProduct['is_liked'];
  handleClick: () => void;
  moduleOrigin?: string;
};

export function RecentlyViewedProductCard({
  id,
  slug,
  pricing,
  sizes,
  pictures,
  preview,
  brandName,
  hasVideo,
  isLiked,
  handleClick,
  moduleOrigin,
}: Props) {
  const t = useTranslations('common');
  const hasMoreThanOnePicture = pictures ? pictures.length > 1 : false;

  const conditionalProps: Partial<ProductCardProps> = {};

  if (hasMoreThanOnePicture) {
    conditionalProps.HoverOverlay = () => (
      <LazyLoadImage
        {...buildProductImageBasicProps(pictures ? pictures[1] : {})}
        className={styles.hoverImage}
        alt=""
      />
    );
  }

  return (
    <ProductCard
      productHref={moduleOrigin ? `${slug}?moduleOrigin=${moduleOrigin}` : slug}
      preview={preview}
      handleClick={handleClick}
      ProductAttributes={() => (
        <ProductCardAttributes
          sizes={sizes}
          brandName={brandName || t('Other')}
          displayPrice={pricing.display_price}
        />
      )}
      Overlay={() => (
        <>
          {id && <ProductCardLike productId={id} isLiked={isLiked} />}
          {hasVideo && (
            <div className={styles.videoIcon}>
              <VideoBasicBoldIcon />
            </div>
          )}
        </>
      )}
      {...conditionalProps}
    />
  );
}
